import {
  getGridSingleSelectOperators,
  GridColDef,
  GridFilterModel,
  GridRowParams,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { UserKeys, getUserList } from '../../api/users-api';
import { Page, UserListEntity } from '../../../types';
import { useNavigate } from 'react-router';
import UsersGridToolbar from './users-grid-toolbar';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../utils/use-user-settings';
import { pagesConfig } from '../../shared/pages-config';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import useGridColumns from '../../shared/hooks/use-grid-columns-latest';
import UserAvatar from '../../shared/user-avatar';
import { appEngineFilterOperators } from '../../shared/app-engine-filter-operators';
import SharedDataGridServer from '../../shared/shared-data-grid-server';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import QuickReportsDialog from '../../shared/quick-reports-dialog';
import { quickReportsConfig } from '../../shared/quick-reports-config';
import StatusLabel from '../../shared/status-label';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PersonOff from '@mui/icons-material/PersonOff';

interface UsersGridProps {
  userRoles: string[];
  changeLogRoles: string[];
  limitedVisEnabled: boolean;
  /**
   * A filter to apply on mount instead of the user's persisted saved-view filter (eg. arriving
   * here via the Active Users dashboard tile). This grid's own status values are lowercase
   * (`'active'`/`'suspended'`/`'archived'`), unlike the indexed grid's capitalized BigQuery
   * values, so the preset's value is lowercased before use.
   */
  presetFilterModel?: GridFilterModel;
}

export default function UsersGrid({
  userRoles,
  changeLogRoles,
  limitedVisEnabled,
  presetFilterModel,
}: UsersGridProps) {
  const navigate = useNavigate();
  const { createEvent } = useAnalytics();
  const { primaryDomain, currentTier, viewType } = useUserSettings();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openQuickReports, setOpenQuickReports] = useState(false);
  const columns: GridColDef<UserListEntity>[] = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: 'User Email',
        editable: false,
        sortable: limitedVisEnabled ? false : true,
        filterable: false,
        flex: 2.5,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.userEmail}
                sx={{ width: 36, height: 36 }}
              />
              <Typography variant="body2">{params.row.userEmail}</Typography>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        editable: false,
        sortable: limitedVisEnabled ? false : true,
        filterable: false,
        flex: 1,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: ['active', 'suspended', 'archived'],
        flex: 1,
        filterOperators: getGridSingleSelectOperators().filter((op) =>
          appEngineFilterOperators.acceptedStatusOperators.includes(op.value)
        ),
        renderCell: (params) => {
          const status = params.row.archived
            ? 'ARCHIVED'
            : params.row.suspended
            ? 'SUSPENDED'
            : 'ACTIVE';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'orgUnitName',
        headerName: 'Org Unit Name',
        flex: 2,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return primaryDomain;
          return row.orgUnitPath.split('/').slice(-1)[0];
        },
      },
      {
        field: 'orgUnitPath',
        headerName: 'Org Unit Path',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return '/' + primaryDomain;
          return row.orgUnitPath;
        },
      },
      {
        field: 'accountCreatedTime',
        headerName: 'Created',
        type: 'dateTime',
        valueGetter: (_value, row) => {
          if (row.accountCreatedTime === 0) return '';
          return new Date(row.accountCreatedTime);
        },
        valueFormatter: (value) => {
          if (value === '') return '';
          return new Date(value as string).toLocaleDateString();
        },
        minWidth: 200,
        editable: false,
        sortable: limitedVisEnabled ? false : true,
        filterable: false,
      },
    ],
    [limitedVisEnabled, primaryDomain]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'userManagementGridStates'
  );

  const presetStatus = presetFilterModel?.items
    .find((item) => item.field === 'status')
    ?.value?.toString()
    .toLowerCase() as 'active' | 'suspended' | 'archived' | undefined;

  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: presetStatus
          ? [{ field: 'status', operator: 'is', value: presetStatus }]
          : [],
      },
    },
    columns: {
      columnVisibilityModel: {
        orgUnitPath: false,
      },
    },
  };

  const [filterModel, setFilterModel] = useState<
    'All' | 'active' | 'suspended' | 'archived'
  >(
    presetStatus || initialState?.filter?.filterModel?.items[0]?.value || 'All'
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    column: string | null;
    sortDirection: string | null;
    search: string;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 25,
    page: initialState?.pagination?.paginationModel?.page || 0,
    cursor: null,
    column: initialState?.sorting?.sortModel?.[0]?.field || null,
    sortDirection:
      initialState?.sorting?.sortModel?.[0]?.sort === 'asc'
        ? 'ASCENDING'
        : initialState?.sorting?.sortModel?.[0]?.sort === 'desc'
        ? 'DESCENDING'
        : null,
    search:
      initialState?.filter?.filterModel?.quickFilterValues?.join(' ') ?? '',
  });

  const { isLoading, data, isFetching } = useQuery({
    queryKey: [
      UserKeys.UserList,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
      paginationModel.column,
      paginationModel.sortDirection,
      paginationModel.search,
    ],
    queryFn: () => {
      const object: Page<UserListEntity[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && data && data.queries) {
        object.queries = {
          [Object.keys(data.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return getUserList(
        object,
        paginationModel.search,
        filterModel,
        paginationModel.column,
        paginationModel.sortDirection
      );
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading user data. Please refresh and try again.',
    },
  });

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      setPaginationModel,
      userRoles,
      selectedRows,
      setSelectedRows,
      changeLogRoles,
      limitedVisEnabled,
      currentTier,
      setOpenQuickReports,
    }),
    [
      changeLogRoles,
      currentTier,
      limitedVisEnabled,
      paginationModel,
      selectedRows,
      userRoles,
    ]
  );

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: useToolbar(UsersGridToolbar, toolbarProps),
          ...createGridOverlays(
            'Users',
            <PersonOff />,
            paginationModel.search !== '' || filterModel !== 'All'
              ? 'Try updating your search or filters.'
              : ''
          ),
        }}
        checkboxSelection={currentTier !== 'essentials'}
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        getRowId={(row) => row.userEmail}
        data={data}
        columns={computedColumns}
        paginationModel={paginationModel}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onPaginationModelChange={(newModel) => {
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: data?.cursor || '',
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
              search: paginationModel.search,
            });
          }
        }}
        onSortModelChange={(newModel) => {
          setPaginationModel({
            page: 0,
            pageSize: paginationModel.pageSize,
            cursor: null,
            column: newModel.length > 0 ? newModel[0].field : null,
            sortDirection:
              newModel.length > 0 && newModel[0].sort === 'asc'
                ? 'ASCENDING'
                : newModel.length > 0 && newModel[0].sort === 'desc'
                ? 'DESCENDING'
                : null,
            search: paginationModel.search,
          });
        }}
        onFilterModelChange={(model) => {
          // Handle status filter
          const statusFilterItem = model.items.find(
            (item) => item.field === 'status'
          );
          const newStatusFilter = (statusFilterItem?.value || 'All') as
            | 'All'
            | 'active'
            | 'suspended'
            | 'archived';

          if (newStatusFilter !== filterModel) {
            createEvent(analytics.user_management.user_grid.filter_grid, {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.name,
              filter: newStatusFilter,
            });
            setFilterModel(newStatusFilter);
          }

          setPaginationModel((prev) => ({
            ...prev,
            page: 0,
            cursor: null,
            search: model.quickFilterValues?.join(' ') || '',
          }));
        }}
        initialState={
          presetStatus ? defaultGridState : initialState || defaultGridState
        }
        onRowClick={(row: GridRowParams<UserListEntity>) => {
          navigate(`/${pagesConfig.users.link}/${row.row.userEmail}`);
        }}
        loading={isLoading || isFetching}
        gridKey={viewType.userManagementGridStates?.activeGridState}
      />
      <QuickReportsDialog
        open={openQuickReports}
        handleClose={() => setOpenQuickReports(false)}
        sourceData={selectedRows}
        sourceType="User"
        dialogTitle={`User Management - ${selectedRows.length} Selected`}
        analyticsEvent={analytics.user_management.user_grid.quick_report}
        contentDescription={`Generate a one-time report for the selected user${
          selectedRows.length > 1 ? 's' : ''
        }`}
        reports={quickReportsConfig}
        changeLogRoles={changeLogRoles}
      />
    </>
  );
}
